import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';
import { PlansBoEnhancedResponse } from '@utils/types/back-office/update-plan-bo';

/**
 * Hook to fetch plans with enhanced data (resolved price plan names, features summary, etc.)
 * Uses GET /api/v3/admin/plan/?enhanced=true
 */
export const useGetPlansBoEnhanced = () => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  // Default to newest first when the user has not picked a column to sort by.
  const sort = (searchParams.sort as string | undefined) || 'created_at';
  const direction =
    (searchParams.direction as 'asc' | 'desc' | undefined) || 'desc';
  const q = (searchParams.q as string | undefined) || undefined;

  const fetchPlans = async () => {
    const { plansBo: plansUrl } = GET_URL_NEW({
      offset,
      limit,
      q,
      sort,
      direction
    });

    // Add enhanced=true query parameter
    const enhancedUrl = plansUrl.includes('?')
      ? `${plansUrl}&enhanced=true`
      : `${plansUrl}?enhanced=true`;

    const { data } = await Axios.get<PlansBoEnhancedResponse>(enhancedUrl);
    return data;
  };

  return useQuery<PlansBoEnhancedResponse, AxiosError<BaseError>>(
    ['plansBoEnhanced', offset, limit, q, sort, direction],
    fetchPlans,
    {
      keepPreviousData: true,
      useErrorBoundary: false
    }
  );
};
